import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { V2Layout, NeonDivider } from './V2Shared';
import SEO from '../components/SEO';
import '../portal-v2/portal-v2.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmtUSD = (v, c = 'USD') => {
  const n = Number(v || 0);
  if (c === 'USD') return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;
};

const Row = ({ label, value, mono = false, testId }) => (
  <div
    data-testid={testId}
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 24,
      padding: '14px 0',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    }}
  >
    <span
      className="v2-mono-tag"
      style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', whiteSpace: 'nowrap' }}
    >
      {label}
    </span>
    <span
      style={{
        fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit',
        fontSize: mono ? 12 : 14,
        textAlign: 'right',
        color: 'rgba(255,255,255,0.92)',
        wordBreak: mono ? 'break-all' : 'normal',
      }}
    >
      {value || '—'}
    </span>
  </div>
);

const V2PaymentSuccess = () => {
  const [params] = useSearchParams();
  // Support: ?payment_intent_id=, ?payment_intent= (Stripe 3DS), ?session_id= (legacy)
  const piId = params.get('payment_intent_id') || params.get('payment_intent') || '';
  const sessionId = params.get('session_id') || '';
  const redirectStatus = params.get('redirect_status'); // succeeded|failed|requires_payment_method
  const continueTo = params.get('continue') || '/';

  const [state, setState] = useState('loading'); // 'loading' | 'success' | 'pending' | 'failed' | 'error'
  const [details, setDetails] = useState(null);

  useEffect(() => {
    document.title = 'Payment Successful · WesTex Legal';
  }, []);

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      attempts += 1;
      try {
        if (piId) {
          const r = await axios.get(`${API}/api/payments/intent/${piId}`);
          if (cancelled) return;
          const d = r.data;
          setDetails(d);
          if (d.status === 'succeeded') {
            setState('success');
            return;
          }
          if (d.status === 'processing' || d.status === 'requires_capture') {
            setState('pending');
          } else if (d.status === 'requires_payment_method' || d.status === 'canceled') {
            setState('failed');
            return;
          }
        } else if (sessionId) {
          // Legacy Checkout Session fallback
          const r = await axios.get(`${API}/api/payments/checkout/status/${sessionId}`);
          if (cancelled) return;
          const d = r.data;
          if (d.payment_status === 'paid') {
            setDetails({
              amount: (d.amount_total || 0) / 100,
              currency: (d.currency || 'usd').toUpperCase(),
              description: d.metadata?.description || `Invoice #${d.metadata?.job_id || d.metadata?.reference_id || ''}`.trim(),
              source: d.metadata?.Source || d.metadata?.source,
              submitted: d.metadata?.Submitted,
              servee: d.metadata?.Servee,
              payment_intent_id: sessionId,
            });
            setState('success');
            return;
          }
          if (d.status === 'expired') {
            setState('failed');
            return;
          }
        } else {
          setState('error');
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, 1800);
        } else {
          // Still not succeeded — show "pending" so the customer isn't stuck on a spinner
          setState((s) => (s === 'loading' ? 'pending' : s));
        }
      } catch (e) {
        if (cancelled) return;
        if (attempts < 3) {
          setTimeout(poll, 1800);
        } else {
          setState('error');
        }
      }
    };

    if (redirectStatus === 'failed' || redirectStatus === 'requires_payment_method') {
      setState('failed');
      return;
    }
    poll();
    return () => { cancelled = true; };
  }, [piId, sessionId, redirectStatus]);

  return (
    <V2Layout>
      <SEO title="Payment Successful · WesTex Legal" path="/payment-success" description="Payment confirmation." />
      <section className="v2-section v2-section-dark" style={{ paddingTop: 120 }}>
        <div className="v2-container">
          <div style={{ maxWidth: 620, margin: '0 auto' }}>

            {state === 'loading' && (
              <div data-testid="pv2-pay-success-loading" style={{ textAlign: 'center', padding: '64px 0', color: 'rgba(255,255,255,0.7)' }}>
                <Loader2 size={32} strokeWidth={1.2} className="pv2-spin" style={{ margin: '0 auto 18px', display: 'block', animation: 'spin 1.1s linear infinite' }} />
                <div className="v2-mono-tag" style={{ fontSize: 11 }}>Confirming Payment</div>
                <div style={{ marginTop: 10, fontSize: 14 }}>Hold tight — this only takes a moment.</div>
              </div>
            )}

            {state === 'success' && details && (
              <div data-testid="pv2-pay-success-ok">
                <div className="v2-mono-tag" style={{ marginBottom: 14 }}>PAYMENT · CONFIRMED</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 14 }}>
                  <CheckCircle2 size={28} strokeWidth={1.4} style={{ color: 'var(--pv2-green-text, #34d399)', flexShrink: 0 }} />
                  <h1 className="v2-about-title" style={{ fontSize: 44, lineHeight: 1.05, margin: 0 }}>
                    Payment <em>received.</em>
                  </h1>
                </div>
                <p style={{ fontSize: 15, lineHeight: 1.65, color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>
                  Thank you. Your payment has been successfully processed and our team has been notified.
                </p>

                <div
                  style={{
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: 'rgba(255,255,255,0.02)',
                    padding: '8px 24px 16px',
                    marginBottom: 28,
                  }}
                >
                  <Row label="Amount" value={fmtUSD(details.amount, details.currency)} testId="pv2-success-amount" />
                  {details.description && <Row label="Reference" value={details.description} testId="pv2-success-ref" />}
                  {details.servee && <Row label="Servee" value={details.servee} testId="pv2-success-servee" />}
                  {details.source && <Row label="Source" value={details.source} testId="pv2-success-source" />}
                  {details.submitted && <Row label="Submitted" value={details.submitted} testId="pv2-success-submitted" />}
                  {details.payment_intent_id && <Row label="Payment ID" value={details.payment_intent_id} mono testId="pv2-success-pi" />}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  <Link
                    to={continueTo}
                    className="pv2-btn pv2-btn-primary"
                    style={{ flex: '1 1 auto', textAlign: 'center', padding: '14px 20px', textDecoration: 'none' }}
                    data-testid="pv2-success-continue"
                  >
                    {continueTo === '/' ? 'Return to homepage' : 'Continue'}
                  </Link>
                  {continueTo !== '/' && (
                    <Link
                      to="/"
                      className="pv2-btn pv2-btn-ghost"
                      style={{ flex: '0 0 auto', textAlign: 'center', padding: '14px 20px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      data-testid="pv2-success-home"
                    >
                      <ArrowLeft size={14} /> Home
                    </Link>
                  )}
                </div>
              </div>
            )}

            {state === 'pending' && (
              <div data-testid="pv2-pay-success-pending">
                <div className="v2-mono-tag" style={{ marginBottom: 14 }}>PAYMENT · PROCESSING</div>
                <h1 className="v2-about-title" style={{ fontSize: 40, lineHeight: 1.1, marginBottom: 14 }}>
                  Almost there.
                </h1>
                <p style={{ fontSize: 15, lineHeight: 1.65, color: 'rgba(255,255,255,0.7)', marginBottom: 20 }}>
                  Your bank is still processing the transaction. You'll receive an email receipt the moment it clears — usually within a minute. You can safely leave this page.
                </p>
                {details?.payment_intent_id && (
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 24 }}>
                    Ref · {details.payment_intent_id}
                  </div>
                )}
                <Link to="/" className="pv2-btn pv2-btn-ghost" style={{ padding: '12px 20px', textDecoration: 'none' }}>Return home</Link>
              </div>
            )}

            {state === 'failed' && (
              <div data-testid="pv2-pay-success-failed">
                <div className="v2-mono-tag" style={{ marginBottom: 14, color: 'var(--pv2-red-bright)' }}>PAYMENT · NOT COMPLETED</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                  <AlertCircle size={26} strokeWidth={1.4} style={{ color: 'var(--pv2-red-bright)' }} />
                  <h1 className="v2-about-title" style={{ fontSize: 38, lineHeight: 1.1, margin: 0 }}>
                    We couldn't process that payment.
                  </h1>
                </div>
                <p style={{ fontSize: 15, lineHeight: 1.65, color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>
                  No charges were made. Please try again, or contact us at (806) 401-1749 if the issue persists.
                </p>
                <div style={{ display: 'flex', gap: 12 }}>
                  <Link to="/" className="pv2-btn pv2-btn-primary" style={{ padding: '14px 20px', textDecoration: 'none' }}>Try again</Link>
                  <Link to="/contact" className="pv2-btn pv2-btn-ghost" style={{ padding: '14px 20px', textDecoration: 'none' }}>Contact us</Link>
                </div>
              </div>
            )}

            {state === 'error' && (
              <div data-testid="pv2-pay-success-error">
                <div className="v2-mono-tag" style={{ marginBottom: 14 }}>STATUS · UNKNOWN</div>
                <h1 className="v2-about-title" style={{ fontSize: 38, lineHeight: 1.1, marginBottom: 14 }}>
                  We can't verify this payment right now.
                </h1>
                <p style={{ fontSize: 15, lineHeight: 1.65, color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>
                  If you received a charge confirmation from your bank or Stripe, the payment did go through — we'll process it shortly. Otherwise please try again or reach out.
                </p>
                <Link to="/" className="pv2-btn pv2-btn-primary" style={{ padding: '14px 20px', textDecoration: 'none' }}>Return home</Link>
              </div>
            )}
          </div>
        </div>
      </section>
      <NeonDivider delay={0.4} />
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </V2Layout>
  );
};

export default V2PaymentSuccess;
